import { Checkbox, Grid } from '@mui/material';
import { dateFormat } from '@nx-smartmonkey/shared/helpers';
import { RoutalDialog, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { planService } from '../application/PlanService';
import { PlanFlat } from '../domain/plan/PlanFlat';
import { PlanFlatDTO } from '../domain/plan/PlanFlatDTO';
import { useRetrieveActiveProject } from '../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../hooks/project/useRetrieveActiveProjectResources';
import { useShowSnackbarMessage } from '../hooks/snackbarMessage/useShowSnackbarMessage';
import { useMoveStopsToANewPlan } from '../hooks/stop/useMoveStopsToANewPlan';
import { useMoveStopsToAPlan } from '../hooks/stop/useMoveStopsToAPlan';
import { PlanFlatMapper } from '../mappers/plan/PlanFlatMapper';
import Button from '../react-components/Button';
import Text from '../react-components/Text';

type MoveStopsDialogProps = {
  open: boolean;
  selectedStopIds: Array<string>;
  currentPlanId?: string;
  onCloseDialog: (...args: any[]) => any;
};

const MoveStopsDialog = ({ open, selectedStopIds, currentPlanId, onCloseDialog }: MoveStopsDialogProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const [plans, setPlans] = useState<PlanFlat[] | undefined>(undefined);
  const [selectedPlan, setSelectedPlan] = useState<PlanFlat | `new` | `unassigned`>(`new`);

  const moveStopsNavigateToDestination = localStorage.getItem(`MOVE_STOPS_NAVIGATE_TO_DESTINATION`);
  const [navigateToDestination, setNavigateToDestination] = useState<boolean>(
    moveStopsNavigateToDestination === `true`
  );

  const {
    run: createPlanWithStops,
    isLoading: isLoadingCreatePlan,
    success: moveStopsToNewSuccess,
  } = useMoveStopsToANewPlan({
    project: activeProject,
    navigateToDestination: navigateToDestination,
  });

  const {
    run: moveStopsToPlan,
    isLoading: isLoadingMoveToPlan,
    success: moveStopsToPlanSuccess,
  } = useMoveStopsToAPlan({
    navigateToDestination: navigateToDestination,
  });

  useEffect(() => {
    (async () => {
      if (open && activeProject && !plans) {
        const response = await planService.search({
          project: activeProject,
          offset: 0,
          limit: 250,
        });
        const retrievedPlans = (response.docs as unknown as PlanFlatDTO[])
          .filter((plan) => plan.id !== currentPlanId)
          .map((plan) => PlanFlatMapper.toDomain(plan));
        setPlans(retrievedPlans);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plans]);

  useEffect(() => {
    if (moveStopsToPlanSuccess || moveStopsToNewSuccess) {
      onCloseDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveStopsToPlanSuccess]);

  const handleMoveStops = (destinationPlan: PlanFlat | `new` | `unassigned`) => {
    if (destinationPlan === `new`) {
      if (selectedStopIds.length > (projectResources?.max_stops_plan ?? 0)) {
        showSnackbarMessage({ messageIntlId: `infoerror.highway.project.error.stops_quota_exceeded` });
        return;
      }

      createPlanWithStops({ stopIds: selectedStopIds });
      return;
    }

    if (destinationPlan !== `unassigned`) {
      const limit = (projectResources?.max_stops_plan ?? 0) - (destinationPlan?.total_stops ?? 0);

      if (selectedStopIds.length > limit) {
        showSnackbarMessage({ messageIntlId: `infoerror.highway.project.error.stops_quota_exceeded` });
        return;
      }

      moveStopsToPlan({ stopIds: selectedStopIds, planId: destinationPlan.id });
      return;
    }

    moveStopsToPlan({ stopIds: selectedStopIds });
  };

  const onMoveStops = (e: any) => {
    e.stopPropagation();
    handleMoveStops(selectedPlan);
  };

  const handleChangeGoToDestination = (value: boolean) => {
    localStorage.setItem(`MOVE_STOPS_NAVIGATE_TO_DESTINATION`, `${value}`);
    setNavigateToDestination(value);
  };

  const isLoading = isLoadingCreatePlan || isLoadingMoveToPlan;

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={onCloseDialog}
      titleContent={<Text variant="h6" intlId="services.move_to_plan_dialog.title" />}
      titleRightContent={
        <Button variant="default" style={{ margin: 0, minWidth: `110px` }} disabled={isLoading} onClick={onMoveStops}>
          {isLoading ? (
            <Text intlId="loading" style={{ color: `white` }} />
          ) : (
            <Text intlId="services.move_to_plan_dialog.button" style={{ color: `white`, cursor: `pointer` }} />
          )}
        </Button>
      }
      showFooter={false}
    >
      <Text intlId="services.move_to_plan_dialog.subtitle" />

      <VSpacer small />

      <Grid container alignItems="center">
        <RoutalSelect
          id="move_to_plan_selector"
          disabled={isLoading}
          popoverProps={{
            options: [
              {
                key: `move_to_new`,
                content: <Text intlId="services.move_to_plan_dialog.option.new" style={{ cursor: `pointer` }} />,
                onClick: () => setSelectedPlan(`new`),
              },
              ...(plans ?? []).map(
                (plan, idx): RoutalSelectMultiSelectOption => ({
                  key: `plan-${plan.id ?? idx}`,
                  content: (
                    <Grid direction="column" justifyContent="center" style={{ width: `348px` }}>
                      {plan.label ? (
                        <Text style={{ cursor: `pointer` }}>{plan.label}</Text>
                      ) : (
                        <Text style={{ cursor: `pointer`, color: RoutalPalette.secondary00 }} intlId="no_name" />
                      )}
                      <Text variant="label" style={{ cursor: `pointer` }}>
                        {dateFormat(plan.created_at)}
                      </Text>
                    </Grid>
                  ),
                  onClick: () => setSelectedPlan(plan),
                })
              ),
              {
                key: `move_to_unassigned`,
                content: <Text style={{ cursor: `pointer` }} intlId="services.move_to_plan_dialog.option.unassigned" />,
                onClick: () => setSelectedPlan(`unassigned`),
              },
            ],
          }}
        >
          <Text style={{ width: `100%`, textAlign: `start` }}>
            {typeof selectedPlan !== typeof `abc` ? (
              <Grid direction="column" justifyContent="center">
                {(selectedPlan as PlanFlat).label ? (
                  <Text style={{ cursor: `pointer` }}>{(selectedPlan as PlanFlat).label}</Text>
                ) : (
                  <Text style={{ color: RoutalPalette.secondary00, cursor: `pointer` }} intlId="no_name" />
                )}
              </Grid>
            ) : (
              <>
                {selectedPlan === `new` ? (
                  <Text intlId="services.move_to_plan_dialog.option.new" style={{ cursor: `pointer` }} />
                ) : (
                  <Text intlId="services.move_to_plan_dialog.option.unassigned" style={{ cursor: `pointer` }} />
                )}
              </>
            )}
          </Text>
        </RoutalSelect>
      </Grid>

      <VSpacer small />

      <Grid container alignItems="center">
        <Checkbox
          style={{ marginLeft: `-12px` }}
          checked={navigateToDestination}
          onChange={(e: any, value: any) => handleChangeGoToDestination(value)}
        />
        <Text
          tagName="span"
          onClick={() => handleChangeGoToDestination(!navigateToDestination)}
          intlId="services.move_to_plan_dialog.go_to_destination_option"
        />
      </Grid>
    </RoutalDialog>
  );
};

MoveStopsDialog.defaultProps = {
  plans: undefined,
};

export default MoveStopsDialog;
